import { useEffect, useMemo, useState } from 'react'
import { LIST_MISSING_SETTINGS } from '../data/listMissingSettings'
import { getPreference, savePreference } from '../api/preferences'
import { listClients } from '../api/clients'
import type { ClientProfile } from '../data/types'

type Props = {
  open: boolean
  onClose: () => void
}

export function MissingProfileDataModal({ open, onClose }: Props) {
  const tabs = LIST_MISSING_SETTINGS.tabs
  const [activeId, setActiveId] = useState<string>(tabs[0]?.id || 'all')
  const [msg, setMsg] = useState('')

  const initialChecked = useMemo(() => {
    const map: Record<string, Record<string, boolean>> = {}
    for (const t of tabs) {
      map[t.id] = {}
      for (const r of t.rows) map[t.id][r.value] = r.checked
    }
    return map
  }, [])

  const [checked, setChecked] = useState(initialChecked)

  useEffect(() => {
    if (!open) return
    void getPreference<{ checked: Record<string, Record<string, boolean>> }>('clients.missing-profile')
      .then((saved) => { if (saved) setChecked(saved.checked) })
  }, [open])

  if (!open) return null

  const active = tabs.find((t) => t.id === activeId) || tabs[0]
  const activeChecked = checked[active.id] || {}
  const selectedCount = Object.values(activeChecked).filter(Boolean).length
  const allSelected =
    active.rows.length > 0 && active.rows.every((r) => !!activeChecked[r.value])

  const toggleAll = (on: boolean) => {
    setChecked((prev) => {
      const next = { ...prev[active.id] }
      for (const r of active.rows) next[r.value] = on
      return { ...prev, [active.id]: next }
    })
  }

  const runAction = async (kind: 'export' | 'save' | 'print') => {
    if (selectedCount === 0) {
      setMsg('Please select at least 1 column.')
      return
    }
    await savePreference('clients.missing-profile', { checked })
    if (kind === 'export' || kind === 'save') {
      const clients = await listClients()
      const selected = active.rows.filter((row) => activeChecked[row.value])
      const rows = clients.map((client) => ({
        id: client.id,
        name: `${client.firstName} ${client.lastName}`.trim(),
        missing: selected.filter((row) => !missingValue(client, row.value)).map((row) => row.label),
      })).filter((row) => row.missing.length)
      const escape = (value: string) => `"${value.replaceAll('"', '""')}"`
      const csv = ['Client ID,Client Name,Missing Fields', ...rows.map((row) =>
        [row.id, row.name, row.missing.join('; ')].map(escape).join(','),
      )].join('\n')
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `client-missing-profile-${active.id}.csv`
      anchor.click()
      URL.revokeObjectURL(url)
    }
    if (kind === 'print') window.print()
    const label =
      kind === 'save'
        ? 'Missing Profile Data settings saved.'
        : kind === 'print'
          ? 'Missing Profile Data sent to print.'
          : 'Missing Profile Data exported.'
    setMsg(label)
    setTimeout(() => {
      setMsg('')
      if (kind !== 'print') onClose()
    }, 700)
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="missing-profile-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__head">
          <h2 id="missing-profile-title">Client Missing Profile Data</h2>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-panel__body">
          <div className="ms-tabs" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={t.id === activeId}
                className={`ms-tab${t.id === activeId ? ' is-active' : ''}`}
                onClick={() => {
                  setActiveId(t.id)
                  setMsg('')
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          <section className="fs-print-section">
            <h3>{active.title}</h3>
            <p className="muted" style={{ margin: '0 0 0.75rem' }}>Missing Column</p>
            <table className="fs-print-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) =>
                        toggleAll((e.target as HTMLInputElement).checked)
                      }
                      aria-label="Select all columns"
                    />
                  </th>
                  <th>Missing column name</th>
                </tr>
              </thead>
              <tbody>
                {active.rows.map((r) => (
                  <tr key={r.value}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!activeChecked[r.value]}
                        onChange={(e) =>
                          setChecked((prev) => ({
                            ...prev,
                            [active.id]: {
                              ...prev[active.id],
                              [r.value]: (e.target as HTMLInputElement).checked,
                            },
                          }))
                        }
                      />
                    </td>
                    <td>{r.label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>

        <div className="modal-panel__foot">
          {msg ? (
            <span className="muted">{msg}</span>
          ) : (
            <span className="muted">{selectedCount} columns selected</span>
          )}
          <div style={{ flex: '1' }} />
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => void runAction('print')}>
            Print
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => void runAction('save')}>
            Save &amp; Export
          </button>
          <button type="button" className="btn btn--primary" onClick={() => void runAction('export')}>
            Export
          </button>
        </div>
      </div>
    </div>
  )
}

const PROFILE_KEYS: Record<string, string> = {
  'MARITAL STATUS': 'cli_material_status', CITY: 'cli_city', STATE: 'cli_state', 'ZIP CODE': 'cli_zip',
  'SOCIAL SECURITY': 'cli_security', GUARDIAN: 'cli_guardian_name', 'PRIMARY LANGUAGE': 'cli_primary_lang',
  'SECONDARY LANGUAGE': 'cli_secondary_lang', ETHNICITY: 'cli_ethenicity', 'CLIENT SIGNATURE FIELD': 'cli_signature_name',
  'SCHEDULED DAYS': 'cli_schedule', 'SCHEDULED SHIFT': 'cli_schedule_shift', 'TRANSPORTATION DAYS': 'cli_tra_schedule',
  'TRANSPORTATION SHIFT': 'cli_et_setting', 'PICK-UP ROUTE': 'cli_trans_route_id', 'DROP-OFF ROUTE': 'cli_trans_drop_route_id',
  'PICK-UP DRIVER': 'cli_trans_driver', 'DROP-OFF DRIVER': 'cli_trans_drop_driver', 'HMO ID': 'cli_hmo_id',
  MEALS: 'cli_schedule_meals', 'CACFP DETERMINATION': 'cli_food_program_type', 'AUTH CODE': 'cli_visitor_auth',
}

function missingValue(client: ClientProfile, field: string): unknown {
  const direct: Record<string, unknown> = {
    'ADMISSION DATE': client.admissionDate, 'CLIENT NAME': `${client.firstName} ${client.lastName}`.trim(),
    'FIRST NAME': client.firstName, 'LAST NAME': client.lastName, 'MIDDLE NAME': client.middleName,
    'DATE OF BIRTH': client.dob, GENDER: client.gender, ADDRESS1: client.address, 'PROFILE PIC': client.photoUrl,
    'PAYER NAME': client.payerName, 'MEDICAID ID': client.medicaidId, 'CLIENT ID': client.id,
    'HOME PHONE': client.phoneHome, 'CELL PHONE': client.phoneCell, 'CLIENT EMAIL ID': client.email,
  }
  const value = direct[field] ?? client.profile?.[PROFILE_KEYS[field]]
  return Array.isArray(value) ? value.length : value
}
